import * as React from 'react';
import {
    Antd3Form,
    Antd3FormProps,
    Antd3Icon,
    Antd3Input,
} from 'common/components/deprecated/antd3';
import { UserPaths } from 'features/user/models/userModel';

import { useDispatch, useSelector } from 'react-redux';
import { getIsUserLoggedIn } from 'features/user/selectors/userSelectors';
import { redirectToUrl } from 'common/actions/navigationAction';

import { AuthContainerProps } from '../containers/AuthContainer';
import {
    LoginPage,
    BrandSide,
    BrandLogo,
    BrandContent,
    BrandTitle,
    BrandDescription,
    BrandFooter,
    BrandFooterLabel,
    BrandPills,
    Pill,
    FormSide,
    FormWrap,
    FormTitle,
    FormSubtitle,
    StyledFormWrapper,
    FieldLabel,
    LoginButton,
    ForgotLink,
    FormFooter,
} from './Auth.styled';

const LOGO_URL =
    'https://storage.googleapis.com/cenefits-static-assets/logo/cenefits-logo-white.png';

type AuthFormProps = AuthContainerProps & Antd3FormProps;

const AuthForm = ({ form, logInRequest, isProcessing }: AuthFormProps) => {
    const dispatch = useDispatch();

    const isUserLoggedIn = useSelector(getIsUserLoggedIn);

    React.useEffect(() => {
        if (isUserLoggedIn) {
            dispatch(redirectToUrl('/'));
        }
    }, [isUserLoggedIn]);

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        form.validateFields((error, { email, password }) => {
            if (!error) {
                logInRequest({ username: email.toLowerCase(), password });
            }
        });
    };

    return (
        <LoginPage>
            <BrandSide>
                <BrandLogo src={LOGO_URL} alt="Cenefits" />
                <BrandContent>
                    <BrandTitle>
                        Deliver
                        <br />
                        <em>Community Benefits</em>
                        <br />
                        at scale.
                    </BrandTitle>
                    <BrandDescription>
                        One platform to manage, measure and report on Community Benefits across
                        projects and organisations.
                    </BrandDescription>
                </BrandContent>
                <BrandFooter>
                    <BrandFooterLabel>Trusted across</BrandFooterLabel>
                    <BrandPills>
                        <Pill>🏴󠁧󠁢󠁳󠁣󠁴󠁿 Scotland</Pill>
                        <Pill>🏴󠁧󠁢󠁷󠁬󠁳󠁿 Wales</Pill>
                    </BrandPills>
                </BrandFooter>
            </BrandSide>

            <FormSide>
                <FormWrap>
                    <FormTitle>Sign in</FormTitle>
                    <FormSubtitle>Enter your credentials to continue</FormSubtitle>

                    <StyledFormWrapper>
                        <Antd3Form onSubmit={handleSubmit}>
                            <Antd3Form.Item>
                                <FieldLabel>Email address</FieldLabel>
                                <Antd3Input
                                    form={form}
                                    id="email"
                                    getFieldDecoratorOptions={{
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Please enter your email address',
                                            },
                                        ],
                                    }}
                                    prefix={<Antd3Icon type="mail" />}
                                    placeholder="name@company.com"
                                    disabled={isProcessing}
                                />
                            </Antd3Form.Item>
                            <Antd3Form.Item>
                                <FieldLabel>Password</FieldLabel>
                                <Antd3Input
                                    form={form}
                                    id="password"
                                    getFieldDecoratorOptions={{
                                        rules: [
                                            {
                                                required: true,
                                                message: 'Please enter your password',
                                            },
                                        ],
                                    }}
                                    prefix={<Antd3Icon type="lock" />}
                                    type="password"
                                    placeholder="Enter your password"
                                    disabled={isProcessing}
                                />
                            </Antd3Form.Item>
                            <Antd3Form.Item>
                                <LoginButton
                                    type="primary"
                                    htmlType="submit"
                                    loading={isProcessing}
                                >
                                    Sign in
                                </LoginButton>
                            </Antd3Form.Item>
                        </Antd3Form>
                    </StyledFormWrapper>

                    <FormFooter>
                        <ForgotLink to={UserPaths.ResetPassword}>Forgot your password?</ForgotLink>
                    </FormFooter>
                </FormWrap>
            </FormSide>
        </LoginPage>
    );
};

export const Auth = Antd3Form.create({})(AuthForm);
